import { useState, useEffect, useCallback } from 'react';
import {
  Image,
  Video,
  FileText,
  Download,
  Package,
  ExternalLink,
  Loader2,
  AlertCircle,
  RefreshCw,
  BarChart3,
  CheckCircle2,
  Clock,
} from 'lucide-react';
import { useStudio } from '../providers/StudioContext';
import type { PlaybookSummary, AdPackDownload, RankedAd } from '../types';

interface PackState {
  playbook: PlaybookSummary | null;
  downloadUrl: string | null;
  rankedAds: RankedAd[];
  loading: boolean;
  error: string | null;
  downloading: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function statusColor(status: string): string {
  switch (status) {
    case 'complete':
    case 'published':
      return 'text-teal-600 bg-teal-50 border-teal-200';
    case 'generating':
    case 'partial':
      return 'text-amber-600 bg-amber-50 border-amber-200';
    case 'failed':
      return 'text-red-600 bg-red-50 border-red-200';
    default:
      return 'text-slate-500 bg-slate-50 border-slate-200';
  }
}

function AssetCountPill({ label, count, icon: Icon }: { label: string; count?: number; icon: React.ElementType }) {
  if (!count) return null;
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 bg-white border border-neutral-200 rounded-full text-xs text-neutral-600">
      <Icon size={11} className="text-neutral-400" />
      <span className="font-medium text-neutral-800">{count}</span>
      <span>{label}</span>
    </div>
  );
}

function PlaybookCard({
  playbookId,
  apiBase,
  clientUid,
}: {
  playbookId: string;
  apiBase: string;
  clientUid: string;
}) {
  const [state, setState] = useState<PackState>({
    playbook: null,
    downloadUrl: null,
    rankedAds: [],
    loading: true,
    error: null,
    downloading: false,
  });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await fetch(`${apiBase}/api/v1/generation/playbooks/${playbookId}`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const data = await res.json();
      const playbook: PlaybookSummary = data.playbook ?? data;

      // Fetch ranked ads / pack summary if pack_id exists
      let rankedAds: RankedAd[] = [];
      if (playbook.pack_id) {
        try {
          const packRes = await fetch(
            `${apiBase}/api/v1/ad-generation/status/${clientUid}/${playbook.pack_id}`
          );
          if (packRes.ok) {
            const packData = await packRes.json();
            rankedAds = packData?.manifest?.ranked_ads ?? [];
          }
        } catch {
          // non-critical
        }
      }

      setState((s) => ({ ...s, playbook, rankedAds, loading: false }));
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: String(e) }));
    }
  }, [apiBase, playbookId, clientUid]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDownload = async () => {
    if (!state.playbook?.pack_id) return;
    setState((s) => ({ ...s, downloading: true }));
    try {
      const res = await fetch(
        `${apiBase}/api/v1/generation/adpacks/${clientUid}/${state.playbook!.pack_id}/download`
      );
      if (!res.ok) throw new Error(`Download failed: ${res.statusText}`);
      const data: AdPackDownload = await res.json();
      if (data.download_url) {
        window.open(data.download_url, '_blank');
      }
    } catch (e) {
      setState((s) => ({ ...s, error: String(e) }));
    } finally {
      setState((s) => ({ ...s, downloading: false }));
    }
  };

  if (state.loading) {
    return (
      <div className="border border-neutral-200 rounded-xl p-4 flex items-center gap-3 text-sm text-neutral-500">
        <Loader2 size={16} className="animate-spin text-neutral-400" />
        <span className="font-mono text-xs text-neutral-400">{playbookId}</span>
      </div>
    );
  }

  if (state.error || !state.playbook) {
    return (
      <div className="border border-red-200 bg-red-50 rounded-xl p-4 flex items-center gap-2 text-xs text-red-600">
        <AlertCircle size={14} />
        <span>Failed to load playbook: {state.error}</span>
        <button onClick={load} className="ml-auto text-red-500 hover:text-red-700">
          <RefreshCw size={12} />
        </button>
      </div>
    );
  }

  const { playbook, rankedAds } = state;
  const summary = playbook.asset_summary ?? {};

  return (
    <div className="border border-neutral-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 p-4 bg-white">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${statusColor(playbook.status)}`}
            >
              {playbook.status === 'complete' || playbook.status === 'published' ? (
                <CheckCircle2 size={10} />
              ) : (
                <Clock size={10} />
              )}
              {playbook.status}
            </span>
            {playbook.campaign_name && (
              <span className="text-xs text-neutral-400">{playbook.campaign_name}</span>
            )}
          </div>
          <p className="text-sm font-semibold text-neutral-800 truncate">{playbook.name}</p>
          <p className="text-[10px] font-mono text-neutral-400 mt-0.5">{playbook.playbook_id}</p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {playbook.pack_id && (
            <button
              onClick={handleDownload}
              disabled={state.downloading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-teal-500 rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50"
              title="Download asset ZIP"
            >
              {state.downloading ? (
                <Loader2 size={11} className="animate-spin" />
              ) : (
                <Download size={11} />
              )}
              Download ZIP
            </button>
          )}
        </div>
      </div>

      {/* Asset counts */}
      {Object.values(summary).some(Boolean) && (
        <div className="flex flex-wrap gap-1.5 px-4 pb-3">
          <AssetCountPill label="angles" count={summary.angles_count} icon={BarChart3} />
          <AssetCountPill label="scripts" count={summary.scripts_count} icon={FileText} />
          <AssetCountPill label="images" count={summary.images_count} icon={Image} />
          <AssetCountPill label="static ads" count={summary.static_ads_count} icon={Package} />
          <AssetCountPill label="videos" count={summary.videos_count} icon={Video} />
        </div>
      )}

      {/* Ranked ad image previews */}
      {rankedAds.length > 0 && (
        <div className="border-t border-neutral-100 px-4 py-3">
          <p className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wider mb-2">
            Top Ads
          </p>
          <div className="grid grid-cols-3 gap-2">
            {rankedAds.slice(0, 6).map((ad, i) => (
              <div
                key={i}
                className="relative rounded-lg overflow-hidden bg-neutral-100 aspect-square border border-neutral-200"
              >
                {ad.image_url ? (
                  <>
                    <img
                      src={ad.image_url}
                      alt={ad.headline ?? `Ad ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {ad.score !== undefined && (
                      <div className="absolute top-1 right-1 bg-black/60 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                        {ad.score.toFixed(1)}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-1 p-2 text-center">
                    <Image size={16} className="text-neutral-300" />
                    {ad.headline && (
                      <p className="text-[9px] text-neutral-400 line-clamp-2">{ad.headline}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cost summary */}
      {playbook.cost_summary?.total_cost_usd !== undefined && (
        <div className="border-t border-neutral-100 px-4 py-2 flex items-center justify-between">
          <span className="text-[10px] text-neutral-400">Generation cost</span>
          <span className="text-xs font-medium text-neutral-600">
            ${playbook.cost_summary.total_cost_usd.toFixed(4)}
          </span>
        </div>
      )}
    </div>
  );
}

export function AssetsPanel() {
  const { brief, clientUid, config } = useStudio();
  const apiBase = config.session?.api_base ?? '';
  const playbookIds = (brief as (typeof brief & { playbook_ids?: string[] }) | null)?.playbook_ids ?? [];

  if (!brief) {
    return (
      <div className="text-center py-16 px-8">
        <div className="flex justify-center gap-4 mb-6 text-neutral-300">
          <Image size={32} />
          <Video size={32} />
          <FileText size={32} />
        </div>
        <h3 className="text-lg font-semibold text-neutral-700 mb-2">Assets</h3>
        <p className="text-neutral-500 max-w-md mx-auto text-sm">
          Select or create a brief to see generated assets, ad packs, and download options.
        </p>
      </div>
    );
  }

  if (playbookIds.length === 0) {
    return (
      <div className="text-center py-16 px-8">
        <Package size={36} className="text-neutral-200 mx-auto mb-4" />
        <h3 className="text-base font-semibold text-neutral-700 mb-2">No Playbooks Yet</h3>
        <p className="text-neutral-500 max-w-sm mx-auto text-sm">
          Use <code className="bg-neutral-100 px-1 rounded text-xs">create_playbook</code> and{' '}
          <code className="bg-neutral-100 px-1 rounded text-xs">run_generation</code> to generate
          assets for this brief.
        </p>
        {apiBase && (
          <a
            href={`${apiBase.replace('/api', '')}/generation`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 mt-4 text-xs text-teal-600 hover:underline"
          >
            <ExternalLink size={12} />
            Open Generation Pipeline
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-neutral-700">
          Ad Packs &amp; Assets
        </h3>
        <span className="text-[10px] text-neutral-400 font-mono">
          {playbookIds.length} playbook{playbookIds.length !== 1 ? 's' : ''}
        </span>
      </div>

      {playbookIds.map((pid) => (
        <PlaybookCard
          key={pid}
          playbookId={pid}
          apiBase={apiBase}
          clientUid={clientUid}
        />
      ))}
    </div>
  );
}
